/**
 * Drives the PDP media slider (Figma 955:3654 / 313:1009).
 *
 * Every image is rendered up front and hidden with the `hidden` attribute rather than
 * having one `<img>` swap its `src`: that keeps each picture's own srcset/sizes and
 * alt text, and means the switch costs no request once an image has loaded.
 *
 * The arrows step the selection instead of scrolling the thumbnail strip. At the five
 * thumbnails the frame draws the strip never overflows, so a scroller would be a control
 * that does nothing; stepping is also what makes the strip usable from the keyboard.
 */
class FilliersProductGallery extends HTMLElement {
  connectedCallback() {
    this.images = Array.from(this.querySelectorAll('[data-gallery-image]'));
    this.thumbs = Array.from(this.querySelectorAll('[data-gallery-thumb]'));
    this.steps = Array.from(this.querySelectorAll('[data-gallery-step]'));
    this.strip = this.querySelector('.filliers-product__thumbs');
    if (this.images.length < 2 || !this.thumbs.length) return;

    this.ids = this.thumbs.map((thumb) => thumb.dataset.galleryThumb);
    this.index = Math.max(
      0,
      this.ids.indexOf(this.querySelector('[data-gallery-thumb][aria-current="true"]')?.dataset.galleryThumb),
    );

    this.onClick = this.onClick.bind(this);
    this.addEventListener('click', this.onClick);
    this.syncSteps();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
  }

  onClick(event) {
    const step = event.target.closest('[data-gallery-step]');
    if (step && this.contains(step)) {
      this.show(this.index + Number(step.dataset.galleryStep));

      return;
    }

    const thumb = event.target.closest('[data-gallery-thumb]');
    if (thumb && this.contains(thumb)) this.show(this.ids.indexOf(thumb.dataset.galleryThumb));
  }

  show(index) {
    if (index < 0 || index >= this.ids.length) return;
    this.index = index;
    const id = this.ids[index];

    this.images.forEach((image) => {
      image.hidden = image.dataset.galleryImage !== id;
    });
    this.thumbs.forEach((thumb) => {
      thumb.setAttribute('aria-current', thumb.dataset.galleryThumb === id ? 'true' : 'false');
    });
    this.syncSteps();
    this.reveal(this.thumbs[index]);
  }

  /*
   * Nudges the strip so the current thumbnail is inside it. Deliberately not
   * `scrollIntoView`: that can scroll an ancestor as well, and here it declined to move the
   * strip at all for the few pixels the mobile row overruns by. Setting `scrollLeft` can
   * only ever move the strip, which is the whole intent.
   */
  reveal(thumb) {
    if (!this.strip) return;
    const strip = this.strip.getBoundingClientRect();
    const box = thumb.getBoundingClientRect();

    if (box.left < strip.left) this.strip.scrollLeft -= strip.left - box.left;
    else if (box.right > strip.right) this.strip.scrollLeft += box.right - strip.right;
  }

  syncSteps() {
    this.steps.forEach((step) => {
      const target = this.index + Number(step.dataset.galleryStep);
      step.disabled = target < 0 || target >= this.ids.length;
    });
  }
}

if (!customElements.get('filliers-product-gallery')) {
  customElements.define('filliers-product-gallery', FilliersProductGallery);
}
